import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNews } from '../hooks/useNews';
import { createNewsPost, updateNewsPost, deleteNewsPost, uploadNewsImage } from '../services/newsService';
import type { NewsPost } from '../types/news';
import NewsImageCarousel from '../components/NewsImageCarousel';
import CommentSection from '../components/CommentSection';
import LikeButton from '../components/LikeButton';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_IMAGES = 5;
const MAX_CONTENT_LENGTH = 5_000;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

const Blog: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { posts, loading: postsLoading, loadingMore, error: postsError, hasMore, refresh, loadMore } = useNews();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPost, setEditingPost] = useState<NewsPost | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [deleteConfirmPost, setDeleteConfirmPost] = useState<NewsPost | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [operationError, setOperationError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const previewsRef = useRef<string[]>([]);
  previewsRef.current = imagePreviews;

  useEffect(() => {
    return () => {
      previewsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    if (editingPost && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editingPost]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDeleteConfirmPost(null);
    };
    if (deleteConfirmPost) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [deleteConfirmPost]);

  const totalImageCount = existingImageUrls.length + imageFiles.length;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    const validFiles: File[] = [];
    const validPreviews: string[] = [];

    for (const file of files) {
      if (totalImageCount + validFiles.length >= MAX_IMAGES) break;
      if (!ACCEPTED_TYPES.includes(file.type)) {
        setSubmitError('Velg gyldige bilder (JPEG, PNG, GIF eller WebP).');
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        setSubmitError(`Bildet "${file.name}" må være mindre enn 5 MB.`);
        continue;
      }
      validFiles.push(file);
      validPreviews.push(URL.createObjectURL(file));
    }

    setSubmitError('');
    setImageFiles((prev) => [...prev, ...validFiles].slice(0, MAX_IMAGES - existingImageUrls.length));
    setImagePreviews((prev) => {
      const combined = [...prev, ...validPreviews];
      const maxNew = MAX_IMAGES - existingImageUrls.length;
      const toKeep = combined.slice(-maxNew);
      const toRevoke = combined.slice(0, Math.max(0, combined.length - maxNew));
      toRevoke.forEach((url) => URL.revokeObjectURL(url));
      return toKeep;
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => {
      const next = prev.filter((_, i) => i !== index);
      URL.revokeObjectURL(prev[index]);
      return next;
    });
  };

  const clearAllImages = () => {
    imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    setImageFiles([]);
    setImagePreviews([]);
    setExistingImageUrls([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const resetForm = () => {
    setTitle('');
    setContent('');
    clearAllImages();
    setShowCreateForm(false);
    setEditingPost(null);
    setSubmitError('');
  };

  const startEdit = (post: NewsPost) => {
    setShowCreateForm(false);
    setEditingPost(post);
    setTitle(post.title);
    setContent(post.content);
    setExistingImageUrls([...post.image_urls]);
    setImageFiles([]);
    setImagePreviews([]);
    setSubmitError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setSubmitError('');
    setIsSubmitting(true);

    try {
      const imageUrls: string[] = [];
      for (const file of imageFiles) {
        const { url, error: uploadErr } = await uploadNewsImage(file, user.id);
        if (uploadErr) {
          setSubmitError(uploadErr);
          setIsSubmitting(false);
          return;
        }
        if (url) imageUrls.push(url);
      }

      const { error } = await createNewsPost(
        { title, content, image_urls: imageUrls },
        user.id
      );

      if (error) {
        setSubmitError((error as Error).message);
        setIsSubmitting(false);
        return;
      }

      resetForm();
      await refresh();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Noe gikk galt');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !editingPost) return;

    setSubmitError('');
    setIsSubmitting(true);

    try {
      const newUrls: string[] = [];
      for (const file of imageFiles) {
        const { url, error: uploadErr } = await uploadNewsImage(file, user.id);
        if (uploadErr) {
          setSubmitError(uploadErr);
          setIsSubmitting(false);
          return;
        }
        if (url) newUrls.push(url);
      }
      const imageUrls = [...existingImageUrls, ...newUrls];

      const { error } = await updateNewsPost(editingPost.id, {
        title,
        content,
        image_urls: imageUrls,
      });

      if (error) {
        setSubmitError((error as Error).message);
        setIsSubmitting(false);
        return;
      }

      resetForm();
      await refresh();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Noe gikk galt');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (postId: string) => {
    if (!user) return;

    setIsDeleting(true);
    setOperationError('');

    const { error } = await deleteNewsPost(postId);
    if (error) {
      setOperationError((error as Error).message);
    } else {
      setDeleteConfirmPost(null);
      await refresh();
    }
    setIsDeleting(false);
  };

  const removeExistingImage = (index: number) => {
    setExistingImageUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('nb-NO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-[#080808] text-white min-h-screen section-padding overflow-hidden">
      <div className="container-responsive">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-12 sm:mb-16">
          <div>
            <h1 className="heading-responsive font-bold text-white mb-4 tracking-tight">
              Blogg
            </h1>
            <p className="text-responsive max-w-2xl" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Siste oppdateringer og innlegg fra Smuglergods
            </p>
          </div>
          {!authLoading && user && (
            <button
              onClick={() => (showCreateForm || editingPost ? resetForm() : setShowCreateForm(true))}
              className="btn-primary py-3 px-6 font-bold whitespace-nowrap"
            >
              {showCreateForm || editingPost ? 'Avbryt' : '+ Nytt innlegg'}
            </button>
          )}
        </div>

        {user && (showCreateForm || editingPost) && (
          <form
            ref={formRef}
            onSubmit={editingPost ? handleEditSubmit : handleSubmit}
            className="card-glow mb-12 p-6 sm:p-8"
          >
            <h2 className="text-xl font-bold text-white mb-6 tracking-tight">
              {editingPost ? 'Rediger innlegg' : 'Opprett blogginnlegg'}
            </h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="news-title" className="block text-sm font-medium text-gray-300 mb-2">
                  Tittel
                </label>
                <input
                  id="news-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  maxLength={200}
                  placeholder="Tittel"
                  className="input-glow w-full"
                />
              </div>

              <div>
                <label htmlFor="news-content" className="block text-sm font-medium text-gray-300 mb-2">
                  Innhold
                </label>
                <textarea
                  id="news-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  required
                  maxLength={MAX_CONTENT_LENGTH}
                  rows={6}
                  placeholder="Skriv blogginnlegget ditt..."
                  className="input-glow w-full resize-y min-h-[120px]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Bilder (valgfritt, opptil {MAX_IMAGES})
                </label>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap gap-3 items-start">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ACCEPTED_TYPES.join(',')}
                      onChange={handleFileSelect}
                      multiple
                      className="hidden"
                    />
                    {totalImageCount < MAX_IMAGES && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="btn-secondary py-2 px-4 text-sm"
                      >
                        Legg til bilder
                      </button>
                    )}
                    {existingImageUrls.map((url, index) => (
                      <div key={url} className="relative group/preview">
                        <img
                          src={url}
                          alt={`Innleggsbilde ${index + 1}`}
                          className="h-24 w-auto object-cover"
                          style={{ border: '1px solid rgba(255,255,255,0.15)' }}
                          loading="lazy"
                        />
                        <button
                          type="button"
                          onClick={() => removeExistingImage(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-600 text-white text-sm hover:bg-red-500 transition-colors"
                          aria-label={`Fjern bilde ${index + 1}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {imagePreviews.map((url, index) => (
                      <div key={url} className="relative group/preview">
                        <img
                          src={url}
                          alt={`Nytt opplastet bilde ${index + 1}`}
                          className="h-24 w-auto object-cover"
                          style={{ border: '1px solid rgba(255,255,255,0.15)' }}
                          loading="lazy"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-600 text-white text-sm hover:bg-red-500 transition-colors"
                          aria-label={`Fjern bilde ${index + 1}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">JPEG, PNG, GIF eller WebP. Maks 5 MB per bilde. Første bilde blir forside.</p>
                </div>
              </div>
            </div>

            {submitError && (
              <p className="mt-4 text-red-400 text-sm">{submitError}</p>
            )}

            <div className="mt-6 flex gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary py-2 px-6 disabled:opacity-50"
              >
                {isSubmitting
                  ? (editingPost ? 'Oppdaterer...' : 'Publiserer...')
                  : (editingPost ? 'Oppdater' : 'Publiser')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="btn-secondary py-2 px-6"
              >
                Avbryt
              </button>
            </div>
          </form>
        )}

        {deleteConfirmPost && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            onClick={() => setDeleteConfirmPost(null)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-modal-title"
          >
            <div
              className="card-glow p-6 sm:p-8 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 id="delete-modal-title" className="text-xl font-bold text-white mb-4">
                Slette innlegg?
              </h2>
              <p className="text-gray-300 mb-6">
                Er du sikker på at du vil slette &quot;{deleteConfirmPost.title}&quot;? Dette kan ikke angres.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => !isDeleting && setDeleteConfirmPost(null)}
                  disabled={isDeleting}
                  className="btn-secondary py-2 px-6 disabled:opacity-50"
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(deleteConfirmPost.id)}
                  disabled={isDeleting}
                  className="py-2 px-6 bg-red-600 text-white rounded hover:bg-red-500 font-medium disabled:opacity-50"
                >
                  {isDeleting ? 'Sletter...' : 'Slett innlegg'}
                </button>
              </div>
            </div>
          </div>
        )}

        {operationError && (
          <div className="mb-6 p-4 rounded border border-red-500/50 bg-red-500/10 text-red-400 text-sm">
            {operationError}
            <button
              type="button"
              onClick={() => setOperationError('')}
              className="ml-2 underline hover:no-underline"
            >
              Lukk
            </button>
          </div>
        )}

        {postsLoading ? (
          <div className="text-center py-16" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Laster blogg...
          </div>
        ) : postsError ? (
          <div className="card-glow p-8 text-center">
            <p className="text-red-400 mb-4">{postsError}</p>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Pass på at Supabase-tabellen <code className="px-1" style={{ background: 'rgba(255,255,255,0.06)' }}>news</code> finnes, og at RLS tillater offentlig lesing. Se SUPABASE_SETUP.md for oppsett.
            </p>
          </div>
        ) : posts.length === 0 ? (
          <div className="card-glow p-12 text-center">
            <p className="text-gray-400 mb-2">Ingen innlegg ennå.</p>
            {user && (
              <p className="text-sm text-gray-500">
                Klikk &quot;+ Nytt innlegg&quot; over for å lage det første.
              </p>
            )}
          </div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-8">
            {posts.map((post) => (
              <article key={post.id} className="card-glow overflow-hidden">
                {post.image_urls.length > 0 && (
                  <NewsImageCarousel images={post.image_urls} alt={post.title} />
                )}
                <div className="p-6 sm:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                    <div>
                      <Link
                        to={`/blog/${post.id}`}
                        className="hover:underline"
                      >
                        <h2 className="text-xl sm:text-2xl font-bold text-white mb-2 tracking-tight">
                          {post.title}
                        </h2>
                      </Link>
                      <p className="text-sm text-gray-500">
                        {formatDate(post.created_at)}
                        {post.author_display_name && (
                          <>
                            {' · '}
                            <Link
                              to={`/users/${post.author_display_name}`}
                            className="link-primary hover:no-underline"
                            >
                              {post.author_display_name}
                            </Link>
                          </>
                        )}
                      </p>
                    </div>
                    {user?.id === post.author_id && (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => startEdit(post)}
                          className="btn-secondary py-2 px-4 text-sm"
                        >
                          Rediger
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirmPost(post)}
                          className="py-2 px-4 text-sm text-red-400 border border-red-400/50 rounded hover:bg-red-400/10"
                        >
                          Slett
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {post.content}
                  </div>
                  <div className="mt-4 flex items-center gap-4">
                    <LikeButton postId={post.id} />
                    <Link
                      to={`/blog/${post.id}`}
                      className="text-sm link-secondary font-medium"
                    >
                      Les innlegg →
                    </Link>
                  </div>
                  <CommentSection postId={post.id} preview={true} />
                </div>
              </article>
            ))}
            {hasMore && (
              <div className="flex justify-center pt-8">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="btn-secondary py-3 px-8 font-bold disabled:opacity-50"
                >
                  {loadingMore ? 'Laster...' : 'Vis mer'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
